import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { getAvailabilityCustom } from 'redux/actions/availability';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import { Button, Tooltip, Typography } from '@mui/material';
import useQuery from 'hooks/useQuery';
import CategoryForm from 'components/MenuNew/FormView/CategoryForm';
import Preloader from 'components/Preloader';
import FormHeader from './FormHeader';
import FormFooter from './FormFooter';
import { confirm } from 'components/MenuNew/ConfirmModal';
import {
  addMenuCategory,
  deleteMenuCategory,
  setCategoryFromSocket,
  updateMenuCategory,
  updateCategoryKioskAvailability,
} from 'redux/actions/Menu/categories';
import { toast } from 'react-toastify';
import UpdateAvailability from '../ProductForm/UpdateAvailability/UpdateAvailability';

import Private from 'components/Private';
import { SOLD_OUT_STATUS } from 'constants/enumSoldOut';
import { getMenusV2 } from 'redux/actions/Menu/menuV2';
import { getCategoryProductsV2 } from 'redux/actions/Menu/products';
import { SET_LOADING } from 'redux/actions/types';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import { useTranslation } from 'react-i18next';
import { getPrinterTags } from 'redux/actions/printers';
import { StyledTab, TabsList } from '../ProductForm/styled';
import PromotionTab from 'pages/PosSettings/Promotions/PromotionTab';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';

function TabPanel(props) {
  const { value, index, content } = props;

  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{content}</Box>}
    </div>
  );
}

const initialData = {
  name: '',
  sort: 0,
  description: '',
  availability: [],
  isActive: true,
  soldOut: {
    status: SOLD_OUT_STATUS.AVAILABILITY,
    soldOutDate: '',
  },
  printerTags: [],
};

const FormData = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const history = useHistory();

  const [availability, handleAvailability] = useState([]);
  const { menu } = useSelector(({ menu }) => menu);
  const categoryFormInfo = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required('field_is_required_')
      .max(60, 'batch_report.category_max_message'),
    printerTags: yup.array(),
  });
  const [isOpen, setIsOpen] = useState(false);
  const { currentBusiness } = useSelector(({ business }) => business);

  const categoryFormMethods = useForm({
    defaultValues: initialData,
    mode: 'all',
    resolver: yupResolver(categoryFormInfo),
  });

  const { reset, getValues, watch, formState, register } = categoryFormMethods;
  const [title, setTitle] = useState(getValues().name);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [kioskAvailable, setKioskAvailable] = useState(null);
  const [submitted, setIsSubmitted] = useState(false);
  const query = useQuery();
  const mode = query.get('mode');
  const id = query.get('cid');
  const isInOverview = history.location.pathname.includes('/overview');
  const isEdit = mode === 'edit';
  const isConnectedToPos = !!watch('connectedProviders')?.pos;
  const promotionsEnabled = usePromotionsEnabled();
  const showPromotionTab = isEdit && isConnectedToPos && promotionsEnabled;

  useEffect(() => {
    setIsSubmitted(false);
    dispatch(getAvailabilityCustom()).then((data) => handleAvailability(data));
    dispatch(getPrinterTags());
  }, []);

  useEffect(() => {
    if (menu.categories) {
      const categoryData = menu?.categories?.find((item) => item._id === id);
      if (isEdit) {
        if (categoryData) {
          setLoading(false);
          setIsSubmitted(false);
          setKioskAvailable(categoryData.availableInKiosk ?? null);
          reset(categoryData, { keepDirty: false });
        }
      } else {
        reset(initialData);
        setKioskAvailable(null);
        setIsSubmitted(false);
        setLoading(false);
      }
    }
  }, [id, mode, menu]);

  const handleValidSubmit = (values) => {
    setLoading(true);
    setIsSubmitted(true);
    if (isEdit) {
      return dispatch(updateMenuCategory(id, values)).finally(() => {
        reset(values, { keepDirty: false });
        setLoading(false);
      });
    }
    return dispatch(addMenuCategory(values))
      .then((res) => {
        history.push(
          `${history.location.pathname}?entityType=category&cid=${res._id}&mode=edit`
        );
        reset(res, { keepDirty: false });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClickOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'confirm_modal.delete_category',
      message: 'menu.are_sure_you_want_to_deleted',
      cancelText: t('orders.cancel'),
      confirmText: t('table_ordering.yes_delete'),
      confirmColor: 'primary',
    });
    if (confirmed) {
      setLoading(true);
      dispatch(deleteMenuCategory(id)).finally(() => {
        history.push(history.location.pathname);
        dispatch(getMenusV2()).then((res) => {
          res.categories.forEach((item) => {
            dispatch(getCategoryProductsV2(item._id)).finally(() => {
              dispatch({ type: SET_LOADING, payload: false });
            });
          });
        });
        reset(initialData);
        dispatch(setCategoryFromSocket());
        setLoading(false);
      });
    }
  };

  useEffect(() => {
    setTitle(getValues().name);
  }, [formState]);

  const isPosConnected = !!getValues()?.connectedProviders?.pos;

  const handleKioskAvailability = (availableInKiosk) => {
    if (!isEdit || !id) return;
    setLoading(true);
    dispatch(updateCategoryKioskAvailability(id, availableInKiosk))
      .then(() => {
        setKioskAvailable(availableInKiosk);
        setTimeout(() => dispatch(getCategoryProductsV2(id)), 2000);
        toast.success(
          availableInKiosk
            ? t('menu.kiosk_items_enabled')
            : t('menu.kiosk_items_disabled')
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  watch((data, { name }) => {
    if (name === 'name') {
      setTitle(getValues().name);
    }
  });

  return (
    <FormProvider {...categoryFormMethods}>
      {isEdit && (
        <UpdateAvailability
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          availability={getValues()}
          currentBusiness={currentBusiness}
          dispatch={dispatch}
          register={register}
          title="category"
        />
      )}
      <form
        style={{ height: '100%', position: 'relative' }}
        onSubmit={categoryFormMethods.handleSubmit(handleValidSubmit)}
      >
        {categoryFormMethods.formState.isDirty &&
        Object.keys(categoryFormMethods.formState.dirtyFields).filter(
          (key) => categoryFormMethods.formState.dirtyFields[key]
        )?.length &&
        !submitted ? (
          <ConfirmPromptModal
            handleConfirm={categoryFormMethods.handleSubmit(handleValidSubmit)}
            submitData={categoryFormMethods.getValues()}
            hasUnsavedChanges={
              categoryFormMethods.formState.isDirty &&
              Object.keys(categoryFormMethods.formState.dirtyFields).filter(
                (key) => categoryFormMethods.formState.dirtyFields[key]
              )?.length &&
              !submitted
            }
            formState={categoryFormMethods}
          />
        ) : (
          <></>
        )}

        {loading && <Preloader overlay={true} />}
        <Box>
          <FormHeader
            mode={mode}
            menu={menu}
            id={id}
            connectedProviders={getValues().connectedProviders}
            title={title}
            handleDelete={handleDelete}
            targetData={getValues()}
            setLoading={setLoading}
          />
        </Box>
        <Box sx={{ height: 'calc(100% - 174.5px)', overflowY: 'auto', py: 3 }}>
          <Box sx={{ px: 4 }}>
            <TabsList
              onChange={(_, tab) => setTabValue(tab)}
              value={tabValue}
              variant="scrollable"
              scrollButtons="auto"
            >
              <StyledTab label={t('menu.basic')} />
              <StyledTab label={t('menu.advanced')} />
              {showPromotionTab && <StyledTab label={t('POS Promotion')} />}
            </TabsList>
          </Box>
          <TabPanel
            value={tabValue}
            index={0}
            content={
              <Private permission="MENU_EDIT" disabled={true}>
                <Box sx={{ px: 4 }}>
                  <CategoryForm
                    data={getValues()}
                    handleClickOpen={handleClickOpen}
                    availability={availability}
                    isInOverview={isInOverview}
                    mode={mode}
                    categoryFormMethods={categoryFormMethods}
                  />
                </Box>
              </Private>
            }
          />
          <TabPanel
            value={tabValue}
            index={1}
            content={
              <Private permission="MENU_EDIT" disabled={true}>
                <Box sx={{ px: 4 }}>
                  <Typography fontWeight={500} fontSize={14} sx={{ mb: 0.5 }}>
                    {t('menu.kiosk_availability')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t('menu.kiosk_availability_description')}
                  </Typography>
                  <Tooltip
                    title={
                      !isPosConnected ? t('menu.pos_required_for_kiosk') : ''
                    }
                    placement="top"
                  >
                    <span>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                          size="small"
                          variant={
                            kioskAvailable === true ? 'contained' : 'outlined'
                          }
                          disabled={!isPosConnected || !isEdit}
                          onClick={() => handleKioskAvailability(true)}
                          sx={{ textTransform: 'none', fontSize: 13, px: 2 }}
                        >
                          {t('menu.make_all_available_in_kiosk')}
                        </Button>
                        <Button
                          size="small"
                          variant={
                            kioskAvailable === false ? 'contained' : 'outlined'
                          }
                          color="error"
                          disabled={!isPosConnected || !isEdit}
                          onClick={() => handleKioskAvailability(false)}
                          sx={{ textTransform: 'none', fontSize: 13, px: 2 }}
                        >
                          {t('menu.make_all_unavailable_in_kiosk')}
                        </Button>
                      </Box>
                    </span>
                  </Tooltip>
                </Box>
              </Private>
            }
          />
          {showPromotionTab && (
            <TabPanel
              value={tabValue}
              index={2}
              content={
                <Box sx={{ px: 4 }}>
                  <PromotionTab
                    lockedCategoryIds={[getValues().uuid]}
                    lockedCategoryName={title || getValues().name}
                  />
                </Box>
              }
            />
          )}
        </Box>
        {!(showPromotionTab && tabValue === 2) && (
          <Box>
            <FormFooter
              isDisable={
                formState.isDirty && Object.keys(formState.dirtyFields)?.length
              }
              mode={mode}
            />
          </Box>
        )}
      </form>
    </FormProvider>
  );
};

export default FormData;
